import { divideRounded } from '@fd/money';
import { addMonths, daysBetween, daysInMonth, startOfMonth, type IsoDate } from './dates.ts';

// Monthly budgets (DECISIONS D-032). A budget is the owner's limit for one expense category (or
// all spending); what was spent comes from the ledger, month by month, in the base currency. Pure,
// so every device shows the same figures.

export type BudgetDefinition = {
  readonly amountMinor: bigint;
  /** What was left (or overspent) moves into the next month. */
  readonly rollover: boolean;
  /** The first month it applies to (any day of that month). */
  readonly effectiveFrom: IsoDate;
  /**
   * The months of the year it applies to, one bit each (January 1 … December 2048); null or
   * missing: every month (D-048). In another month its amount is nothing.
   */
  readonly monthMask?: number | null;
};

/** Whether a budget with this month mask applies in the month of `date`. */
export function budgetAppliesIn(monthMask: number | null | undefined, date: IsoDate): boolean {
  return monthMask === null || monthMask === undefined || (monthMask & (1 << (Number(date.slice(5, 7)) - 1))) !== 0;
}

/** How the month is going against the time that has passed. */
export type BudgetPace = 'ON_TRACK' | 'AHEAD' | 'OVER';

export type BudgetStatus = {
  readonly month: IsoDate;
  /** It applies this month (D-048): a once-a-year or some-months budget is idle in the others. */
  readonly active: boolean;
  readonly spentMinor: bigint;
  /** Carried from earlier months (negative after overspending); 0 without rollover. */
  readonly carryMinor: bigint;
  /** The budget plus what was carried. */
  readonly limitMinor: bigint;
  /** Left to spend (negative when over). */
  readonly remainingMinor: bigint;
  /** Share of the limit spent, 0–10 000 and beyond. */
  readonly usedBasisPoints: number;
  /** What steady spending would have used by today. */
  readonly expectedMinor: bigint;
  /** Where the month ends at the pace so far; the spending itself once the month is over. */
  readonly projectedMinor: bigint;
  /** Per day for the days left (today included); null once the month is over or nothing is left. */
  readonly perDayLeftMinor: bigint | null;
  readonly daysLeft: number;
  readonly pace: BudgetPace;
};

/** A month's spending on what the budget covers: positive, refunds already netted. */
export type MonthlySpending = ReadonlyMap<IsoDate, bigint>;

const monthsBetween = (from: IsoDate, to: IsoDate) =>
  (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 + Number(to.slice(5, 7)) - Number(from.slice(5, 7));

/**
 * The budget in the month of `month`, on `today`. Spending is read from `spending` by the first
 * day of each month. Pace compares with the share of the month gone (today counts as gone):
 * over the limit is OVER, more than a tenth above the steady line is AHEAD.
 */
export function budgetStatus(budget: BudgetDefinition, spending: MonthlySpending, month: IsoDate, today: IsoDate): BudgetStatus {
  const first = startOfMonth(month);
  const from = startOfMonth(budget.effectiveFrom);
  const amountIn = (m: IsoDate) => (budgetAppliesIn(budget.monthMask, m) ? budget.amountMinor : 0n);
  let carry = 0n;
  if (budget.rollover) {
    for (let m = from; m < first; m = addMonths(m, 1)) carry += amountIn(m) - (spending.get(m) ?? 0n);
  }
  const spent = spending.get(first) ?? 0n;
  const active = budgetAppliesIn(budget.monthMask, first);
  const limit = amountIn(first) + carry;
  const [y, mo] = first.split('-').map(Number) as [number, number];
  const days = daysInMonth(y, mo);
  const current = startOfMonth(today) === first;
  const future = today < first;
  const gone = future ? 0 : current ? daysBetween(first, today) + 1 : days;
  const daysLeft = days - gone + (current ? 1 : 0);
  const expected = limit > 0n ? divideRounded(limit * BigInt(gone), BigInt(days), 'HALF_EVEN') : 0n;
  const projected = current && gone > 0 ? divideRounded(spent * BigInt(days), BigInt(gone), 'HALF_EVEN') : spent;
  const remaining = limit - spent;
  const pace: BudgetPace = spent > limit ? 'OVER' : spent * 10n > expected * 11n && spent > 0n ? 'AHEAD' : 'ON_TRACK';
  return {
    month: first,
    active,
    spentMinor: spent,
    carryMinor: carry,
    limitMinor: limit,
    remainingMinor: remaining,
    usedBasisPoints: limit > 0n ? Number((spent * 10_000n) / limit) : spent > 0n ? 10_000 : 0,
    expectedMinor: expected,
    projectedMinor: projected,
    perDayLeftMinor: current && remaining > 0n && daysLeft > 0 ? divideRounded(remaining, BigInt(daysLeft), 'FLOOR') : null,
    daysLeft: current ? daysLeft : future ? days : 0,
    pace,
  };
}

/**
 * A starting amount for a new budget: the average of the last full months (three by default,
 * only those with the category already in use), rounded up to the next £10.
 */
export function suggestBudget(spending: MonthlySpending, today: IsoDate, months = 3): bigint | null {
  const thisMonth = startOfMonth(today);
  const values: bigint[] = [];
  for (let i = 1; i <= months; i++) {
    const value = spending.get(addMonths(thisMonth, -i));
    if (value !== undefined && value > 0n) values.push(value);
  }
  if (values.length === 0) return null;
  const average = values.reduce((a, b) => a + b, 0n) / BigInt(values.length);
  const step = 1_000n;
  return ((average + step - 1n) / step) * step;
}

/** Months from `from` to `to` inclusive (first days), for the history under a budget. */
export function monthsFromTo(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  const count = monthsBetween(startOfMonth(from), startOfMonth(to));
  for (let i = 0; i <= count; i++) out.push(addMonths(startOfMonth(from), i));
  return out;
}
